import 'reflect-metadata';
import { Container } from 'inversify';
import {
  type Config,
  type Logger,
  TYPES,
  pinoLoggerFactory,
  type Utils,
} from '@bizzdesign/sdk-internal';
import { BrowserUtils } from '@bizzdesign/sdk-internal/browser';
import { BizzdesignSdk } from '../sdk/bizzdesign-sdk';
import { configureSharedDependencyInjection } from '../inversify';
import {
  type AuthClient,
  type AuthTokenProvider,
  configureAmplify,
} from '@bizzdesign/sdk-auth';
import {
  AuthorizationCodeAuthClient,
  CognitoAuthorizationCodeFlowProvider,
} from '@bizzdesign/sdk-auth/browser';

function configureBrowserDependencyInjection(config: Config): Container {
  const container = new Container({ defaultScope: 'Singleton' });
  container.bind<Config>(TYPES.Config).toConstantValue(config);
  container
    .bind<Logger>(TYPES.Logger)
    .toConstantValue(
      pinoLoggerFactory(config.logSettings?.minimumLogLevel ?? 'debug'),
    );
  container.bind<AuthClient>(TYPES.AuthClient).to(AuthorizationCodeAuthClient);
  if (config.authentication.type === 'cognito-user') {
    container
      .bind<AuthTokenProvider>(TYPES.AuthTokenProvider)
      .to(CognitoAuthorizationCodeFlowProvider);
  } else {
    throw new Error(
      'Only interactive Cognito authentication is supported in the browser',
    );
  }
  container.bind<Utils>(TYPES.Utils).to(BrowserUtils);

  configureAmplify(config);
  configureSharedDependencyInjection(container);

  return container;
}

class BizzdesignBrowserSdk extends BizzdesignSdk {
  constructor(config: Config) {
    super(configureBrowserDependencyInjection(config));
  }
}

export function sdkFactory(config: Config): BizzdesignSdk {
  return new BizzdesignBrowserSdk(config);
}
